import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  TextField,
  InputAdornment,
  Badge,
  Divider,
  CircularProgress,
  Chip,
  IconButton,
  Collapse,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Search, Chat, Group, Person, Forum, PersonAdd, ExpandMore, ExpandLess, Add, GroupAdd } from '@material-ui/icons';
import ChatWindow from '../ChatWindow';
import { getConversationsApi, getDMCandidatesApi, createOrFindDMApi, createChannelChatroomApi, IDMCandidate } from '../../api/chatrooms';
import toastr from 'toastr';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    height: 'calc(100vh - 400px)',
    minHeight: '500px',
  },
  sidebar: {
    width: 280,
    borderRight: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.paper,
  },
  searchContainer: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  searchField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 20,
      backgroundColor: theme.palette.grey[100],
    },
  },
  sectionTitle: {
    padding: theme.spacing(1.5, 2),
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.grey[50],
  },
  conversationList: {
    flex: 1,
    overflowY: 'auto',
  },
  conversationItem: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&.selected': {
      backgroundColor: theme.palette.primary.light + '22',
      borderLeft: `3px solid ${theme.palette.primary.main}`,
    },
  },
  chatArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  noChatSelected: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    color: theme.palette.text.secondary,
  },
  unreadBadge: {
    '& .MuiBadge-badge': {
      backgroundColor: theme.palette.primary.main,
      color: 'white',
    },
  },
  pinnedIcon: {
    fontSize: 14,
    color: theme.palette.warning.main,
    marginLeft: 4,
  },
  peopleSearchField: {
    margin: theme.spacing(1, 2),
    '& .MuiOutlinedInput-root': {
      borderRadius: 16,
      backgroundColor: theme.palette.grey[100],
    },
    '& .MuiOutlinedInput-input': {
      padding: '8px 12px',
      fontSize: 13,
    },
  },
  roleChip: {
    height: 18,
    fontSize: 10,
    marginLeft: 4,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  newGroupButton: {
    margin: theme.spacing(1, 2),
    textTransform: 'none',
  },
  memberCheckbox: {
    padding: theme.spacing(0.5),
    marginRight: 0,
  },
  selectedMemberChip: {
    margin: theme.spacing(0.5),
    height: 24,
    fontSize: 12,
  },
  memberList: {
    maxHeight: 250,
    overflowY: 'auto',
  },
}));

interface ChannelChatsViewProps {
  channelIdentifier: string;
  communityIdentifier: string;
  programId: string;
  userId: string;
  userName: string;
  mainColor?: string;
  secondaryColor?: string;
  initialChatId?: string;
  onChatSelect?: (chatId: string | null) => void;
  userRole?: string; // admin | CommunityAdmin | moderator | member
}

interface Conversation {
  room: any;
  unread_count: number;
  last_message_preview?: string;
  last_message_at?: string | Date;
  dm_pinned?: boolean;
  dm_archived?: boolean;
}

export default function ChannelChatsView({
  channelIdentifier,
  communityIdentifier,
  programId,
  userId,
  userName,
  mainColor = '#1976d2',
  secondaryColor = '#ffffff',
  initialChatId,
  onChatSelect,
  userRole = 'member',
}: ChannelChatsViewProps) {
  const classes = useStyles();

  // Check if user can create groups (mods and above)
  const canCreateGroup = ['admin', 'CommunityAdmin', 'moderator'].includes(userRole);

  // State
  const [loading, setLoading] = useState(true);
  const [channelChats, setChannelChats] = useState<any[]>([]);
  const [communityChats, setCommunityChats] = useState<any[]>([]);
  const [directMessages, setDirectMessages] = useState<Conversation[]>([]);
  const [selectedChat, setSelectedChat] = useState<{ id: string; name: string; type: 'group' | 'dm' } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // People section state
  const [peopleExpanded, setPeopleExpanded] = useState(false);
  const [dmCandidates, setDmCandidates] = useState<IDMCandidate[]>([]);
  const [peopleSearch, setPeopleSearch] = useState('');
  const [loadingPeople, setLoadingPeople] = useState(false);
  const [startingDM, setStartingDM] = useState<string | null>(null); // userId being DM'd

  // Create group dialog state
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupMemberSearch, setGroupMemberSearch] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<IDMCandidate[]>([]);
  const [creatingGroup, setCreatingGroup] = useState(false);

  // Reset selectedChat when channel changes so restore logic can run fresh
  useEffect(() => {
    setSelectedChat(null);
  }, [channelIdentifier]);

  // Load conversations
  useEffect(() => {
    const loadConversations = async () => {
      if (!communityIdentifier) return;

      setLoading(true);
      try {
        const result = await getConversationsApi(communityIdentifier, channelIdentifier);
        if (result.success && result.data) {
          const channelChatsData = result.data.channelChats || [];
          const communityChatsData = result.data.communityChats || [];
          const directMessagesData = result.data.directMessages || [];

          setChannelChats(channelChatsData);
          setCommunityChats(communityChatsData);
          setDirectMessages(directMessagesData);

          // Restoration priority:
          // 1. initialChatId from URL (explicit navigation)
          // 2. localStorage persisted chat (returning to channel)
          // 3. Auto-select first channel chat (first visit)

          if (initialChatId) {
            // Search in all chat types
            const allChats = [
              ...channelChatsData.map((c: any) => ({ ...c, type: 'group' as const })),
              ...communityChatsData.map((c: any) => ({ ...c, type: 'group' as const })),
              ...directMessagesData.map((c: any) => ({ room: c.room, type: 'dm' as const })),
            ];

            const foundChat = allChats.find(
              (c) => (c._id || c.room?._id) === initialChatId
            );

            if (foundChat) {
              if (foundChat.type === 'dm') {
                const otherUser = (foundChat.room?.participants || []).find(
                  (p: any) => p._id !== userId
                ) || { name: 'Unknown' };
                setSelectedChat({
                  id: foundChat.room._id,
                  name: otherUser.name,
                  type: 'dm',
                });
              } else {
                setSelectedChat({
                  id: foundChat._id,
                  name: foundChat.room_name,
                  type: 'group',
                });
              }
            }
          } else {
            // Try localStorage
            let restored = false;
            try {
              const stored = localStorage.getItem(`cr_active_chat_${channelIdentifier}`);
              if (stored) {
                const parsed = JSON.parse(stored);
                // Validate the stored chat exists in loaded conversations
                const allChatIds = [
                  ...channelChatsData.map((c: any) => c._id),
                  ...communityChatsData.map((c: any) => c._id),
                  ...directMessagesData.map((c: any) => c.room?._id),
                ];
                if (parsed.id && allChatIds.includes(parsed.id)) {
                  setSelectedChat(parsed);
                  restored = true;
                }
              }
            } catch {}

            // Fallback: auto-select first channel chat
            if (!restored && channelChatsData.length > 0) {
              const firstChat = channelChatsData[0];
              setSelectedChat({
                id: firstChat._id,
                name: firstChat.room_name,
                type: 'group',
              });
            }
          }
        }
      } catch (error) {
        console.error('Error loading conversations:', error);
      } finally {
        setLoading(false);
      }
    };

    loadConversations();
  }, [communityIdentifier, channelIdentifier, initialChatId, userId]);

  // Filter conversations by search query
  const filteredChannelChats = useMemo(() => {
    if (!searchQuery) return channelChats;
    const query = searchQuery.toLowerCase();
    return channelChats.filter((chat) =>
      chat.room_name?.toLowerCase().includes(query)
    );
  }, [channelChats, searchQuery]);

  const filteredCommunityChats = useMemo(() => {
    if (!searchQuery) return communityChats;
    const query = searchQuery.toLowerCase();
    return communityChats.filter((chat) =>
      chat.room_name?.toLowerCase().includes(query)
    );
  }, [communityChats, searchQuery]);

  const filteredDirectMessages = useMemo(() => {
    if (!searchQuery) return directMessages;
    const query = searchQuery.toLowerCase();
    return directMessages.filter((conv) => {
      // Search in participant names
      const participants = conv.room?.participants || [];
      return participants.some((p: any) =>
        p.name?.toLowerCase().includes(query) ||
        p.email?.toLowerCase().includes(query)
      );
    });
  }, [directMessages, searchQuery]);

  // Get other participant in DM
  const getOtherParticipant = (room: any) => {
    const participants = room?.participants || [];
    return participants.find((p: any) => p._id !== userId) || { name: 'Unknown' };
  };

  // Load DM candidates when People section is expanded
  useEffect(() => {
    const loadDMCandidates = async () => {
      if (!peopleExpanded || !programId || !communityIdentifier) return;

      setLoadingPeople(true);
      try {
        const result = await getDMCandidatesApi(
          programId,
          communityIdentifier,
          channelIdentifier,
          peopleSearch || undefined
        );
        if (result.success && result.data) {
          setDmCandidates(result.data);
        }
      } catch (error) {
        console.error('Error loading DM candidates:', error);
      } finally {
        setLoadingPeople(false);
      }
    };

    loadDMCandidates();
  }, [peopleExpanded, programId, communityIdentifier, channelIdentifier, peopleSearch]);

  // Filter DM candidates by search
  const filteredCandidates = useMemo(() => {
    if (!peopleSearch) return dmCandidates;
    const query = peopleSearch.toLowerCase();
    return dmCandidates.filter(
      (c) =>
        c.name?.toLowerCase().includes(query) ||
        c.lastName?.toLowerCase().includes(query) ||
        c.email?.toLowerCase().includes(query)
    );
  }, [dmCandidates, peopleSearch]);

  // Start a DM with a user
  const handleStartDM = useCallback(async (candidate: IDMCandidate) => {
    if (startingDM) return; // Prevent double-clicks

    setStartingDM(candidate.userId);
    try {
      const result = await createOrFindDMApi({
        targetId: candidate.userId,
        programId,
        communityIdentifier,
        contextChannel: channelIdentifier,
      });

      if (result.success && result.data) {
        const room = result.data;
        const chatInfo = {
          id: room._id,
          name: `${candidate.name}${candidate.lastName ? ' ' + candidate.lastName : ''}`,
          type: 'dm' as const,
        };
        setSelectedChat(chatInfo);
        // Persist active chatroom for this channel
        try {
          localStorage.setItem(`cr_active_chat_${channelIdentifier}`, JSON.stringify(chatInfo));
        } catch {}
        // Collapse people section after starting DM
        setPeopleExpanded(false);
        // Notify parent to update URL
        onChatSelect?.(room._id);
        toastr.success(`Started conversation with ${candidate.name}`);
      } else {
        toastr.error('Failed to start conversation');
      }
    } catch (error) {
      console.error('Error starting DM:', error);
      toastr.error('Failed to start conversation');
    } finally {
      setStartingDM(null);
    }
  }, [startingDM, programId, communityIdentifier, channelIdentifier, onChatSelect]);

  // Get role color for chip
  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return '#f44336';
      case 'CommunityAdmin': return '#9c27b0';
      case 'moderator': return '#2196f3';
      default: return '#9e9e9e';
    }
  };

  // Get role label
  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin': return 'Admin';
      case 'CommunityAdmin': return 'Com. Admin';
      case 'moderator': return 'Mod';
      default: return '';
    }
  };

  // Handle chat selection
  const handleSelectChat = (chat: any, type: 'group' | 'dm') => {
    const chatId = chat._id || chat.room?._id;
    const chatInfo = {
      id: chatId,
      name: type === 'dm'
        ? getOtherParticipant(chat.room || chat).name
        : (chat.room_name || chat.room?.room_name),
      type,
    };
    setSelectedChat(chatInfo);
    // Persist active chatroom for this channel
    try {
      localStorage.setItem(`cr_active_chat_${channelIdentifier}`, JSON.stringify(chatInfo));
    } catch {}
    // Notify parent to update URL
    onChatSelect?.(chatId);
  };

  // Toggle member selection for group creation
  const handleToggleMember = (candidate: IDMCandidate) => {
    setSelectedMembers((prev) => {
      const exists = prev.some((m) => m.userId === candidate.userId);
      if (exists) {
        return prev.filter((m) => m.userId !== candidate.userId);
      }
      return [...prev, candidate];
    });
  };

  // Filter candidates for group creation
  const filteredGroupCandidates = useMemo(() => {
    if (!groupMemberSearch) return dmCandidates;
    const query = groupMemberSearch.toLowerCase();
    return dmCandidates.filter(
      (c) =>
        c.name?.toLowerCase().includes(query) ||
        c.lastName?.toLowerCase().includes(query) ||
        c.email?.toLowerCase().includes(query)
    );
  }, [dmCandidates, groupMemberSearch]);

  // Create ad-hoc group
  const handleCreateGroup = async () => {
    if (!groupName.trim()) {
      toastr.error('Please enter a group name');
      return;
    }
    if (selectedMembers.length === 0) {
      toastr.error('Please select at least one member');
      return;
    }

    setCreatingGroup(true);
    try {
      const result = await createChannelChatroomApi({
        programId,
        communityIdentifier,
        channelIdentifier,
        roomName: groupName.trim(),
        accessControl: {
          type: 'specific-users',
          allowedUserIds: [userId, ...selectedMembers.map((m) => m.userId)],
        },
        settings: {
          allowThreading: true,
          allowFileUploads: true,
          allowLinkUnfurling: true,
        },
      });

      if (result.success && result.data) {
        // Select the new group
        const chatInfo = {
          id: result.data._id,
          name: groupName.trim(),
          type: 'group' as const,
        };
        setSelectedChat(chatInfo);
        // Persist active chatroom for this channel
        try {
          localStorage.setItem(`cr_active_chat_${channelIdentifier}`, JSON.stringify(chatInfo));
        } catch {}
        onChatSelect?.(result.data._id);
        // Refresh conversations
        setChannelChats((prev) => [result.data, ...prev]);
        // Close dialog and reset
        setCreateGroupOpen(false);
        setGroupName('');
        setSelectedMembers([]);
        setGroupMemberSearch('');
        toastr.success(`Group "${groupName.trim()}" created!`);
      } else {
        toastr.error('Failed to create group');
      }
    } catch (error) {
      console.error('Error creating group:', error);
      toastr.error('Failed to create group');
    } finally {
      setCreatingGroup(false);
    }
  };

  // Open create group dialog and load candidates
  const handleOpenCreateGroup = () => {
    setCreateGroupOpen(true);
    // Load DM candidates if not already loaded
    if (dmCandidates.length === 0) {
      setPeopleExpanded(true); // This triggers loading
    }
  };

  // Format time for sidebar
  const formatTime = (date?: Date | string) => {
    if (!date) return '';
    const d = new Date(date);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return d.toLocaleDateString([], { weekday: 'short' });
    } else {
      return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress style={{ color: mainColor }} />
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {/* Sidebar */}
      <Box className={classes.sidebar}>
        {/* Search */}
        <Box className={classes.searchContainer}>
          <TextField
            placeholder="Search conversations..."
            variant="outlined"
            size="small"
            fullWidth
            className={classes.searchField}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* New Group Button - only visible to mods/admins */}
        {canCreateGroup && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<GroupAdd />}
            className={classes.newGroupButton}
            onClick={handleOpenCreateGroup}
            style={{ borderColor: mainColor, color: mainColor }}
          >
            New Group
          </Button>
        )}

        {/* Conversation Lists */}
        <Box className={classes.conversationList}>
          {/* This Channel Section */}
          {filteredChannelChats.length > 0 && (
            <>
              <Typography className={classes.sectionTitle}>
                <Forum fontSize="small" style={{ verticalAlign: 'middle', marginRight: 8 }} />
                This Channel
              </Typography>
              <List dense disablePadding>
                {filteredChannelChats.map((chat) => (
                  <ListItem
                    key={chat._id}
                    className={`${classes.conversationItem} ${
                      selectedChat?.id === chat._id ? 'selected' : ''
                    }`}
                    onClick={() => handleSelectChat(chat, 'group')}
                  >
                    <ListItemAvatar>
                      <Avatar style={{ backgroundColor: mainColor, width: 36, height: 36 }}>
                        <Chat fontSize="small" />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={chat.room_name}
                      primaryTypographyProps={{
                        noWrap: true,
                        style: { fontWeight: 500, fontSize: 14 },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}

          {/* Community Chats Section */}
          {filteredCommunityChats.length > 0 && (
            <>
              <Divider />
              <Typography className={classes.sectionTitle}>
                <Group fontSize="small" style={{ verticalAlign: 'middle', marginRight: 8 }} />
                Community Chats
              </Typography>
              <List dense disablePadding>
                {filteredCommunityChats.map((chat) => (
                  <ListItem
                    key={chat._id}
                    className={`${classes.conversationItem} ${
                      selectedChat?.id === chat._id ? 'selected' : ''
                    }`}
                    onClick={() => handleSelectChat(chat, 'group')}
                  >
                    <ListItemAvatar>
                      <Avatar style={{ backgroundColor: '#9c27b0', width: 36, height: 36 }}>
                        <Group fontSize="small" />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={chat.room_name}
                      primaryTypographyProps={{
                        noWrap: true,
                        style: { fontWeight: 500, fontSize: 14 },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}

          {/* Direct Messages Section */}
          {filteredDirectMessages.length > 0 && (
            <>
              <Divider />
              <Typography className={classes.sectionTitle}>
                <Person fontSize="small" style={{ verticalAlign: 'middle', marginRight: 8 }} />
                Direct Messages
              </Typography>
              <List dense disablePadding>
                {filteredDirectMessages.map((conv) => {
                  const otherUser = getOtherParticipant(conv.room);
                  return (
                    <ListItem
                      key={conv.room?._id}
                      className={`${classes.conversationItem} ${
                        selectedChat?.id === conv.room?._id ? 'selected' : ''
                      }`}
                      onClick={() => handleSelectChat(conv, 'dm')}
                    >
                      <ListItemAvatar>
                        <Badge
                          badgeContent={conv.unread_count}
                          className={classes.unreadBadge}
                          max={99}
                        >
                          <Avatar style={{ width: 36, height: 36 }}>
                            {otherUser.name?.charAt(0) || 'U'}
                          </Avatar>
                        </Badge>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center">
                            <Typography
                              variant="body2"
                              noWrap
                              style={{
                                fontWeight: conv.unread_count > 0 ? 600 : 400,
                                flex: 1,
                              }}
                            >
                              {otherUser.name || 'Unknown'}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              style={{ marginLeft: 8 }}
                            >
                              {formatTime(conv.last_message_at)}
                            </Typography>
                          </Box>
                        }
                        secondary={conv.last_message_preview}
                        secondaryTypographyProps={{
                          noWrap: true,
                          style: {
                            fontSize: 12,
                            fontWeight: conv.unread_count > 0 ? 500 : 400,
                          },
                        }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </>
          )}

          {/* People Section - Start new DMs */}
          <Divider />
          <Box
            className={`${classes.sectionTitle} ${classes.sectionHeader}`}
            onClick={() => setPeopleExpanded(!peopleExpanded)}
          >
            <Box display="flex" alignItems="center">
              <PersonAdd fontSize="small" style={{ marginRight: 8 }} />
              People
            </Box>
            <IconButton size="small" style={{ padding: 0 }}>
              {peopleExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
          </Box>
          <Collapse in={peopleExpanded}>
            <TextField
              placeholder="Search people..."
              variant="outlined"
              size="small"
              fullWidth
              className={classes.peopleSearchField}
              value={peopleSearch}
              onChange={(e) => setPeopleSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" style={{ fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
            />
            {loadingPeople ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <List dense disablePadding style={{ maxHeight: 200, overflowY: 'auto' }}>
                {filteredCandidates.map((candidate) => (
                  <ListItem
                    key={candidate.userId}
                    className={classes.conversationItem}
                    onClick={() => handleStartDM(candidate)}
                    disabled={startingDM === candidate.userId}
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={candidate.avatarUrl}
                        style={{ width: 36, height: 36 }}
                      >
                        {candidate.name?.charAt(0) || 'U'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center">
                          <Typography variant="body2" noWrap style={{ fontWeight: 500 }}>
                            {candidate.name}{candidate.lastName ? ` ${candidate.lastName}` : ''}
                          </Typography>
                          {getRoleLabel(candidate.role) && (
                            <Chip
                              label={getRoleLabel(candidate.role)}
                              size="small"
                              className={classes.roleChip}
                              style={{ backgroundColor: getRoleColor(candidate.role), color: 'white' }}
                            />
                          )}
                        </Box>
                      }
                      secondary={candidate.email}
                      secondaryTypographyProps={{ noWrap: true, style: { fontSize: 11 } }}
                    />
                    {startingDM === candidate.userId && (
                      <CircularProgress size={16} style={{ marginLeft: 8 }} />
                    )}
                  </ListItem>
                ))}
                {filteredCandidates.length === 0 && !loadingPeople && (
                  <Box p={2} textAlign="center">
                    <Typography variant="body2" color="textSecondary">
                      {peopleSearch ? 'No people found' : 'No people available'}
                    </Typography>
                  </Box>
                )}
              </List>
            )}
          </Collapse>

          {/* Empty State */}
          {filteredChannelChats.length === 0 &&
            filteredCommunityChats.length === 0 &&
            filteredDirectMessages.length === 0 &&
            !peopleExpanded && (
              <Box p={3} textAlign="center">
                <Chat style={{ fontSize: 48, color: '#ccc', marginBottom: 8 }} />
                <Typography variant="body2" color="textSecondary">
                  No conversations yet
                </Typography>
              </Box>
            )}
        </Box>
      </Box>

      {/* Chat Area */}
      <Box className={classes.chatArea}>
        {selectedChat ? (
          <ChatWindow
            roomId={selectedChat.id}
            roomName={selectedChat.name}
            userId={userId}
            userName={userName}
          />
        ) : (
          <Box className={classes.noChatSelected}>
            <Forum style={{ fontSize: 64, marginBottom: 16, opacity: 0.3 }} />
            <Typography variant="h6">Select a conversation</Typography>
            <Typography variant="body2">
              Choose from your channel chats, community groups, or direct messages
            </Typography>
          </Box>
        )}
      </Box>

      {/* Create Group Dialog */}
      <Dialog
        open={createGroupOpen}
        onClose={() => setCreateGroupOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <GroupAdd style={{ marginRight: 8, color: mainColor }} />
            Create New Group
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Group Name"
            placeholder="Enter group name..."
            variant="outlined"
            fullWidth
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            style={{ marginBottom: 16 }}
          />

          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
            Select Members
          </Typography>

          {/* Selected members chips */}
          {selectedMembers.length > 0 && (
            <Box mb={2} display="flex" flexWrap="wrap">
              {selectedMembers.map((m) => (
                <Chip
                  key={m.userId}
                  label={`${m.name}${m.lastName ? ' ' + m.lastName : ''}`}
                  onDelete={() => handleToggleMember(m)}
                  className={classes.selectedMemberChip}
                  size="small"
                />
              ))}
            </Box>
          )}

          {/* Search members */}
          <TextField
            placeholder="Search people..."
            variant="outlined"
            size="small"
            fullWidth
            value={groupMemberSearch}
            onChange={(e) => setGroupMemberSearch(e.target.value)}
            style={{ marginBottom: 8 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {/* Member list */}
          <List dense className={classes.memberList}>
            {filteredGroupCandidates.map((candidate) => {
              const isSelected = selectedMembers.some((m) => m.userId === candidate.userId);
              return (
                <ListItem
                  key={candidate.userId}
                  button
                  onClick={() => handleToggleMember(candidate)}
                >
                  <Checkbox
                    checked={isSelected}
                    className={classes.memberCheckbox}
                    style={{ color: isSelected ? mainColor : undefined }}
                  />
                  <ListItemAvatar>
                    <Avatar src={candidate.avatarUrl} style={{ width: 32, height: 32 }}>
                      {candidate.name?.charAt(0) || 'U'}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center">
                        <Typography variant="body2">
                          {candidate.name}{candidate.lastName ? ` ${candidate.lastName}` : ''}
                        </Typography>
                        {getRoleLabel(candidate.role) && (
                          <Chip
                            label={getRoleLabel(candidate.role)}
                            size="small"
                            className={classes.roleChip}
                            style={{ backgroundColor: getRoleColor(candidate.role), color: 'white' }}
                          />
                        )}
                      </Box>
                    }
                    secondary={candidate.email}
                    secondaryTypographyProps={{ style: { fontSize: 11 } }}
                  />
                </ListItem>
              );
            })}
            {filteredGroupCandidates.length === 0 && (
              <Box p={2} textAlign="center">
                <Typography variant="body2" color="textSecondary">
                  No people found
                </Typography>
              </Box>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateGroupOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateGroup}
            disabled={creatingGroup || !groupName.trim() || selectedMembers.length === 0}
            style={{ backgroundColor: mainColor, color: 'white' }}
          >
            {creatingGroup ? <CircularProgress size={20} /> : 'Create Group'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
